import {
  createDatabaseBindings,
  Failure,
  ModernIDB,
  Success,
  type DatabaseAppMachineMethods,
} from "@indietabletop/appkit";
import type { Greathelm } from "../../types/lib/index.ts";
import { initDatabase } from "./migrations.ts";

export type KnightlyOrderNotFound = {
  type: "WARBAND_NOT_FOUND";
};

class WarbandsStore {
  private db: GreathelmIDB;

  constructor(db: GreathelmIDB) {
    this.db = db;
  }

  /**
   * Puts the provided value into knightlyOrders store.
   *
   * Note that this method should only really be used during sync actions. In
   * all other cases, you should use {@link updateOne}, which makes sure that
   * the `updatedTs` property is updated, and `snapshotsId` is cleared.
   */
  async putOne(data: Greathelm.WarbandData | Greathelm.Tombstone) {
    const key = await this.db.putToStore("warbands", data);

    // We know that our keys are strings
    return new Success(key as string);
  }

  async getOne(id: string) {
    const gang = await this.db.getFromStore("warbands", id);

    if (!gang || gang.deleted) {
      return new Failure<KnightlyOrderNotFound>({
        type: "WARBAND_NOT_FOUND",
      });
    }

    return new Success(gang);
  }

  async getAll() {
    const [tx, done] = this.db.transaction("warbands", "readonly");
    const [gangs] = await Promise.all([tx.index("createdTs").getAll(), done]);

    return new Success(gangs.filter((gang) => !gang.deleted));
  }

  /**
   * Updates the provided value in knightlyOrders store.
   *
   * Note that this feature should generally be used over {@link putOne}, as
   * it updates updatedTs property and resets snapshotId unless explicitly
   * allowed.
   */
  async updateOne(
    data: Greathelm.WarbandData,
    options?: { allowSetSnapshotId?: boolean },
  ) {
    return this.putOne({
      ...data,

      // Always reset snapshotId if a change is made unless explicitly allowed.
      snapshotId: options?.allowSetSnapshotId ? data.snapshotId : null,
      updatedTs: Date.now(),
    });
  }

  async deleteOne(data: Greathelm.WarbandData) {
    return this.putOne({
      id: data.id,
      name: data.name,
      deleted: true,
      updatedTs: Date.now(),
    });
  }
}

export type GreathelmSchema = {
  warbands: Greathelm.WarbandData | Greathelm.Tombstone;
};

export type GreathelmIndexes = {
  warbands: "createdTs" | "updatedTs" | "rulesetVersion";
};

type GreathelmIDB = ModernIDB<GreathelmSchema, GreathelmIndexes>;

type UserGameData = {
  greathelm?: Greathelm.GameDataGroups;
};

export class GreathelmDatabase
  extends ModernIDB<GreathelmSchema, GreathelmIndexes>
  implements DatabaseAppMachineMethods<UserGameData>
{
  warbands: WarbandsStore;

  constructor() {
    super({
      name: "greathelm",
      version: 1,
      onInit: initDatabase,
      // onUpgrade: migrateDatabase,
    });

    this.warbands = new WarbandsStore(this);
  }

  async getUpdatedGameDataSince(params: {
    sinceTs: number | null;
    exclude: Set<string>;
  }) {
    const { sinceTs, exclude } = params;

    const [knightlyOrdersStore, done] = this.transaction("warbands");
    const knightlyOrdersIdx = knightlyOrdersStore.index("updatedTs");

    // If a timestamp is provided, we use it as the lower bound, getting all
    // items that were updated after the specified time. Otherwise we use an
    // unbounded range by using `null` (retrieves all items).
    const range = sinceTs && IDBKeyRange.lowerBound(sinceTs);

    const [knightlyOrders] = await Promise.all([
      knightlyOrdersIdx.getAll(range),
      done,
    ]);

    return new Success({
      greathelm: {
        warbands: knightlyOrders.filter((order) => !exclude.has(order.id)),
      },
    });
  }

  async upsertGameData(data: UserGameData) {
    const { greathelm: { warbands = [] } = {} } = data;

    if (warbands.length === 0) {
      return new Success([]);
    }

    const [knightlyOrdersStore, done] = this.transaction(
      "warbands",
      "readwrite",
    );
    const [knightlyOrdersKeys] = await Promise.all([
      Promise.all(warbands.map((o) => knightlyOrdersStore.put(o))),
      done,
    ]);

    return new Success(knightlyOrdersKeys as string[]);
  }

  /**
   * This method should perform a complete cleanup of locally persisted data.
   *
   * It should be used for when a user is logging out, or when a logged-out
   * user is explicitly choosing to delete all data.
   *
   * Note that this covers more than IDB: appkit calls this from `logout` and
   * `clientLogout`, so it is the app's single hook for wiping local state, and
   * anything we persist outside IDB has to be cleaned up here too.
   */
  async clearAll() {
    // TODO: clear the cart here too — the context-based cart store doesn't
    // expose a reset hook yet (see src/cart/cart.tsx).

    // Using a record to make sure TS guarantees that all stores are included
    const record: Record<keyof GreathelmSchema, true> = {
      warbands: true,
    };

    const [stores, done] = this.transaction(
      Object.keys(record) as Array<keyof GreathelmSchema>,
      "readwrite",
    );

    await Promise.all([...stores.map((store) => store.clear()), done]);

    return new Success("Cleared.");
  }
}

export const database = new GreathelmDatabase();

Object.assign(window, { spacegits: database });

export const {
  useQuery,
  useDatabase,
  useDatabaseOpenRequest,
  DatabaseProvider,
} = createDatabaseBindings(database);
